import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
    filterNotices,
    getSafeNoticeUrl,
    htmlToPlainText,
    isNoticeActive,
    sanitizeNoticeText,
    toSafeDate,
    validateNoticeDraft
} from "../utils/noticeUtils";

const NOTICES_COLLECTION = "notices";

const normalizeNotice = (notice) => ({
    ...notice,
    createdAt: toSafeDate(notice.createdAt),
    updatedAt: toSafeDate(notice.updatedAt),
    startAt: toSafeDate(notice.startAt),
    expiryAt: toSafeDate(notice.expiryAt)
});

const buildNoticePayload = (input, adminId) => {
    const title = sanitizeNoticeText(input?.title);
    const contentHtml = String(input?.contentHtml || "").trim();
    const contentText = htmlToPlainText(contentHtml);
    const linkUrl = getSafeNoticeUrl(input?.linkUrl);
    const priority = String(input?.priority || "medium").toLowerCase();
    const isVisible = input?.isVisible !== false;
    const startAt = input?.startAt ? new Date(input.startAt) : null;
    const expiryAt = input?.expiryAt ? new Date(input.expiryAt) : null;
    const draft = { title, contentHtml, priority, linkUrl, startAt, expiryAt };
    const validation = validateNoticeDraft(draft);
    if (!validation.isValid) {
        const firstError = Object.values(validation.errors)[0];
        throw new Error(firstError || "Invalid notice data");
    }
    return {
        title,
        contentHtml,
        contentText,
        linkUrl,
        priority,
        isVisible,
        startAt: startAt || serverTimestamp(),
        expiryAt: expiryAt || null,
        createdBy: adminId
    };
};

export const noticeService = {
    async createNotice(input, adminId) {
        const payload = buildNoticePayload(input, adminId);
        const docRef = await addDoc(collection(db, NOTICES_COLLECTION), {
            ...payload,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
        return { id: docRef.id, ...payload };
    },

    async updateNotice(noticeId, input, adminId) {
        const payload = buildNoticePayload(input, adminId);
        await updateDoc(doc(db, NOTICES_COLLECTION, noticeId), {
            ...payload,
            updatedAt: serverTimestamp()
        });
        return { id: noticeId, ...payload };
    },

    async deleteNotice(noticeId) {
        await deleteDoc(doc(db, NOTICES_COLLECTION, noticeId));
        return true;
    },

    async setNoticeVisibility(noticeId, isVisible) {
        await updateDoc(doc(db, NOTICES_COLLECTION, noticeId), {
            isVisible: !!isVisible,
            updatedAt: serverTimestamp()
        });
        return true;
    },

    subscribeAllNotices(onData, onError) {
        const q = query(collection(db, NOTICES_COLLECTION), orderBy("createdAt", "desc"));
        return onSnapshot(
            q,
            (snapshot) => {
                const notices = snapshot.docs.map((docSnap) => normalizeNotice({ id: docSnap.id, ...docSnap.data() }));
                onData(notices);
            },
            onError
        );
    },

    subscribeVisibleNotices(options, onData, onError) {
        const q = query(collection(db, NOTICES_COLLECTION), orderBy("createdAt", "desc"));
        return onSnapshot(
            q,
            (snapshot) => {
                const now = new Date();
                let notices = snapshot.docs
                    .map((docSnap) => normalizeNotice({ id: docSnap.id, ...docSnap.data() }))
                    .filter((notice) => isNoticeActive(notice, now));
                notices = filterNotices(notices, options);
                onData(notices);
            },
            onError
        );
    }
};

export default noticeService;
